import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { ProductState } from '@platform-frontend/checkout';
import { PaymentMethodType } from '@platform-frontend/marketplace';
import { tap } from 'rxjs';
import { AddPaymentMethodService } from '../../commercial-common/add-payment-method.service';

@UntilDestroy()
@Component({
  selector: 'yat-add-first-payment-method',
  templateUrl: './add-first-payment-method.component.html',
  styleUrls: ['./add-first-payment-method.component.scss']
})
export class AddFirstPaymentMethodComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private addPaymentMethodService: AddPaymentMethodService, private readonly store: Store) {}

  @ViewChild('slide') slideRef!: ElementRef<HTMLDivElement>;

  allowedPaymentMethods: PaymentMethodType[] = [];
  currentIndex: number = 0;
  numberOfVisibleItems: number = 0;

  private isProgrammaticScroll: boolean = false;
  private targetScrollLeft: number = 0;
  private isFirstLoad: boolean = true;

  ngOnInit(): void {
    this.store
      .select(ProductState.allowedPaymentMethods)
      .pipe(
        tap(methods => this.allowedPaymentMethodsChangedTo(methods)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private allowedPaymentMethodsChangedTo(methods: PaymentMethodType[]): void {
    this.allowedPaymentMethods = methods;
    this.numberOfVisibleItems = methods.length > 2 ? 3 : methods.length;

    if (this.isFirstLoad) {
      this.currentIndex = 0;
      this.isFirstLoad = false;
      if (this.slideRef) {
        this.scrollToIndex(this.currentIndex);
      }
    }

    // If current index is beyond the new array length, adjust it
    const maxPossibleIndex = Math.max(0, methods.length - this.numberOfVisibleItems);
    if (this.currentIndex > maxPossibleIndex) {
      this.currentIndex = maxPossibleIndex;
      // Need to scroll since index changed
      if (this.slideRef) {
        this.scrollToIndex(this.currentIndex);
      }
    }
  }

  prevMethod(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.scrollToIndex(this.currentIndex);
    }
  }

  nextMethod(): void {
    if (this.currentIndex < this.allowedPaymentMethods.length - this.numberOfVisibleItems) {
      this.currentIndex++;
      this.scrollToIndex(this.currentIndex);
    }
  }

  private scrollToIndex(index: number): void {
    if (this.allowedPaymentMethods.length > this.numberOfVisibleItems) {
      const slide = this.slideRef.nativeElement;
      const cardWidth = this.numberOfVisibleItems > 0 ? slide.offsetWidth / this.numberOfVisibleItems : slide.offsetWidth;
      this.isProgrammaticScroll = true;
      this.targetScrollLeft = index * cardWidth;

      slide.scrollTo({
        left: this.targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;

    if (this.isProgrammaticScroll) {
      const scrollLeft = target.scrollLeft;
      const difference = Math.abs(scrollLeft - this.targetScrollLeft);

      if (difference < 1) {
        this.isProgrammaticScroll = false;
      } else {
        return;
      }
    }

    const cardWidth = this.numberOfVisibleItems > 0 ? target.offsetWidth / this.numberOfVisibleItems : target.offsetWidth;
    const newIndex = Math.round(target.scrollLeft / cardWidth);
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
    }
  }

  onChildFocus(index: number): void {
    const startVisibleIndex = this.currentIndex;
    const endVisibleIndex = this.currentIndex + this.numberOfVisibleItems - 1;

    if (index < startVisibleIndex) {
      while (this.currentIndex > index) {
        this.prevMethod();
      }
    } else if (index > endVisibleIndex) {
      while (this.currentIndex < index - (this.numberOfVisibleItems - 1)) {
        this.nextMethod();
      }
    }
  }

  addPaymentMethod(type: PaymentMethodType): void {
    this.addPaymentMethodService.addPaymentMethodNavigation(type, this.activatedRoute);
  }
}
